import { useEffect, useMemo, useState } from "react";

import { useFormContext } from "react-hook-form";

import {
  abortPreviousRequestsIfAny,
  getQuoteUrlKey,
  getServiceabilityBuildingUrlKey,
} from "@/abort";
import { IIRecommendationResponse } from "@/contract/generated/electrify-v1";
import { useServiceability } from "@/features/SolarForm/useServiceability";
import { useRedirect } from "@/hooks/useRedirect";

import { useQuote } from "./useQuote";
import { getMedianSizeIndex } from "./utils";

type useSolarOutlookProps = {
  hardStopRoute: () => Promise<string>;
};

export const useSolarOutlook = ({ hardStopRoute }: useSolarOutlookProps) => {
  const { goNextRoute } = useRedirect();
  const { getValues, setValue } = useFormContext();
  const { checkBuildingStructure } = useServiceability();
  const [loading, setLoading] = useState(false);
  const [solarOutlook, setSolarOutlook] = useState<IIRecommendationResponse>(
    {}
  );
  const { getQuote } = useQuote();

  const navigateHardStop = async (error?: Error) => {
    if (error?.message === "ROOF_NOT_ENOUGH_FOR_RECOMMENDATION") {
      setValue("hardStop", "solarOutlookRoofSize");
    } else {
      setValue("hardStop", "recommendation");
    }

    const to = await hardStopRoute();
    goNextRoute(to);
  };

  const doCheck = async () => {
    setLoading(true);
    const storeys = Number.parseInt(
      getValues("userStoreys") ?? getValues("accurassiStoreys"),
      10
    );
    const roofMaterial = getValues("roofMaterial");
    const result = await checkBuildingStructure(roofMaterial, storeys);

    if (result && result.error) {
      setLoading(false);
      await navigateHardStop();

      return;
    }

    const { error, result: quoteResult } = await getQuote();

    setLoading(false);
    if (error || !quoteResult) {
      await navigateHardStop(error);

      return;
    }

    setSolarOutlook(quoteResult);
  };

  useEffect(() => {
    doCheck();

    return () => {
      // clean up
      abortPreviousRequestsIfAny(
        getServiceabilityBuildingUrlKey(),
        getQuoteUrlKey()
      );
    };
  }, []);

  const medianSizeIndex = useMemo(() => {
    return getMedianSizeIndex({ solarOutlook });
  }, [solarOutlook]);

  return {
    loading,
    navigateHardStop,
    solarOutlook,
    medianSizeIndex,
  };
};
